import React from 'react';
import { View, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { StackingRow } from '@/components/shared/redesign/StackingRow';

interface ModalFooterButtonConfig {
  label: string;
  onPress: () => void;
  disabled?: boolean;
}

interface ModalFooterProps {
  /** Variant determines the button layout */
  variant: 'single' | 'double';
  /** Primary/confirm button config */
  primaryButton: ModalFooterButtonConfig;
  /** Secondary/cancel button config (only used for 'double' variant) */
  secondaryButton?: ModalFooterButtonConfig;
  /** Show loading state on primary button */
  isLoading?: boolean;
}

/**
 * Reusable Modal Footer component
 * Supports single button (Done) and double button (Cancel/Confirm) layouts
 */
export const ModalFooter: React.FC<ModalFooterProps> = ({
  variant,
  primaryButton,
  secondaryButton,
  isLoading = false,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  if (variant === 'single') {
    return (
      <StackingRow style={styles.footerButtons} stackedStyle={styles.footerButtonsStacked}>
        <TouchableOpacity
          style={[styles.primaryButton, { backgroundColor: MARKETPLACE_COLORS.accentBlueSolid }]}
          onPress={primaryButton.onPress}
          activeOpacity={0.8}
          disabled={primaryButton.disabled || isLoading}
        >
          {isLoading ? (
            <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
          ) : (
            <Text style={[styles.primaryButtonText, { color: MARKETPLACE_COLORS.textPrimary }]} numberOfLines={2}>{primaryButton.label}</Text>
          )}
        </TouchableOpacity>
      </StackingRow>
    );
  }

  return (
    <StackingRow style={styles.footerButtons} stackedStyle={styles.footerButtonsStacked}>
      {secondaryButton && (
        <TouchableOpacity
          style={[styles.secondaryButton, { borderColor: MARKETPLACE_COLORS.textPrimary }]}
          onPress={secondaryButton.onPress}
          activeOpacity={0.8}
          disabled={secondaryButton.disabled || isLoading}
        >
          <Text style={[styles.secondaryButtonText, { color: MARKETPLACE_COLORS.textPrimary }]} numberOfLines={2}>{secondaryButton.label}</Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity
        style={[styles.primaryButton, { backgroundColor: MARKETPLACE_COLORS.accentBlueSolid }]}
        onPress={primaryButton.onPress}
        activeOpacity={0.8}
        disabled={primaryButton.disabled || isLoading}
      >
        {isLoading ? (
          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.textPrimary} />
        ) : (
          <Text style={[styles.primaryButtonText, { color: MARKETPLACE_COLORS.textPrimary }]} numberOfLines={2}>{primaryButton.label}</Text>
        )}
      </TouchableOpacity>
    </StackingRow>
  );
};

const styles = StyleSheet.create({
  footerButtons: {
    flexDirection: 'row',
    gap: 20,
  },
  footerButtonsStacked: {
    gap: 12,
  },
  primaryButton: {
    flex: 1,
    minHeight: 40,
    paddingVertical: 9.5, // 40 − 21 line, split
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
    textAlign: 'center',
  },
  secondaryButton: {
    flex: 1,
    minHeight: 40,
    paddingVertical: 8.5, // 40 − 21 line − 2×1 border, split
    borderWidth: 1,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
    textAlign: 'center',
  },
});

export default ModalFooter;
